import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { userService } from '../services/apiService';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
} from '../i18n';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();
  const syncingRef = useRef(false);
  const lastUserIdRef = useRef(null);

  const language = normalizeLanguage(i18n.language);

  const applyLocalLanguage = useCallback(async (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    }
    if (normalizeLanguage(i18n.language) !== normalized) {
      await i18n.changeLanguage(normalized);
    }
    return normalized;
  }, [i18n]);

  const setLanguage = useCallback(async (nextLanguage) => {
    const normalized = await applyLocalLanguage(nextLanguage);
    if (!isAuthenticated || !user?.id) return normalized;

    try {
      syncingRef.current = true;
      const response = await userService.updatePreferredLanguage(normalized);
      const updatedUser = response?.data?.user;
      if (updatedUser) updateUser(updatedUser);
    } catch {
      /* preferência local permanece mesmo se a API falhar */
    } finally {
      syncingRef.current = false;
    }

    return normalized;
  }, [applyLocalLanguage, isAuthenticated, updateUser, user?.id]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'en' ? 'en' : 'pt-BR';
    }
  }, [language]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      lastUserIdRef.current = null;
      return;
    }

    const preferred = normalizeLanguage(user.preferredLanguage || DEFAULT_LANGUAGE);
    const userChanged = lastUserIdRef.current !== user.id;
    lastUserIdRef.current = user.id;

    if (syncingRef.current) return;
    if (!userChanged && preferred === language) return;

    applyLocalLanguage(preferred);
  }, [applyLocalLanguage, isAuthenticated, language, user?.id, user?.preferredLanguage]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage deve ser usado dentro de um LanguageProvider');
  }
  return context;
}
